import "./index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./providers/AuthProvider";
import { TenantProvider } from "./providers/TenantProvider";
import { RoutesProvider } from "./routes/RoutesProvider";
import type { RouteDefinition } from "./routes/types";
import { MainLayout } from "./components/layout/MainLayout";

// Screens
import ClientsScreen from "./screens/clients/ClientsScreen";
import UsersScreen from "./screens/users/UsersScreen";
import DevicesScreen from "./screens/devices/DevicesScreen";
import MediaLibraryScreen from "./screens/media/MediaLibraryScreen";
import ScheduleEditorScreen from "./screens/schedule-editor/ScheduleEditorScreen";
import SlideshowsScreen from "./screens/slideshows/SlideshowsScreen";
import LoginScreen from "./screens/login/LoginScreen";
import SettingsScreen from "./screens/settings/SettingsScreen";

// Route definitions - single source of truth for navigation and routing
const routes: RouteDefinition[] = [
  // Admin Panel
  {
    path: "/clients",
    label: "Clients",
    section: "Admin Panel",
    element: <ClientsScreen />,
    requirePlatformSuperAdmin: true,
  },
  {
    path: "/users",
    label: "Users",
    section: "Admin Panel",
    element: <UsersScreen />,
    requiredPermission: "users.read",
  },
  {
    path: "/devices",
    label: "Devices",
    section: "Admin Panel",
    element: <DevicesScreen />,
    requiredPermission: "devices.read",
  },
  // Content
  {
    path: "/media",
    label: "Media Library",
    section: "Content",
    element: <MediaLibraryScreen />,
    requiredPermission: "media.read",
  },
  {
    path: "/schedules",
    label: "Schedules",
    section: "Content",
    element: <ScheduleEditorScreen />,
    requiredPermission: "schedules.read",
  },
  {
    path: "/slideshows",
    label: "Slideshows",
    section: "Content",
    element: <SlideshowsScreen />,
    requiredPermission: "schedules.read",
  },
  {
    path: "/settings",
    label: "Settings",
    element: <SettingsScreen />,
    showInNavigation: false,
  },
];

// Smart redirect that finds first accessible route
const SmartRedirect = () => {
  const { hasPermission, permissions } = useAuth();
  const isPlatformSuperAdmin = permissions?.isPlatformSuperAdmin ?? false;

  // Find the first route the user has permission for
  const defaultRoute = routes.find((route) => {
    if (route.requirePlatformSuperAdmin && !isPlatformSuperAdmin) return false;
    if (!route.requiredPermission) return true;
    return hasPermission(route.requiredPermission);
  });

  return <Navigate to={defaultRoute?.path ?? "/media"} replace />;
};

// Inner app that uses auth state
const AppContent = () => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading state while checking stored auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-zinc-400">Loading...</div>
      </div>
    );
  }

  // Not authenticated - show login
  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  // Authenticated - show main app
  return (
    <TenantProvider>
      <RoutesProvider routes={routes}>
        <Routes>
          <Route element={<MainLayout />}>
            {routes.map((route) => (
              <Route key={route.path} path={route.path} element={route.element} />
            ))}
            <Route path="/" element={<SmartRedirect />} />
          </Route>
        </Routes>
      </RoutesProvider>
    </TenantProvider>
  );
};

export default () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
};
